import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import { useDispatch, useSelector } from 'react-redux';
import {
  AppModal,
  AppNotificationAlert,
  AppRemixIcon,
} from '@/layout/AppComponents';
import { ProgressBar } from 'react-bootstrap';
import ReactDOMServer from 'react-dom/server';
import { useParams } from "react-router-dom";
import colors from "../../../constants/colors";
import { axiosPath } from "../../../routing/helpers/router";
import PreventNavigation from "../../components/helpers/PreventNavigation";
import Translator from "../../components/helpers/Translator";
import AppLink from "../../../routing/helpers/AppLink";
import useIsMounted from "../../components/hooks/useIsMounted";
import errorHandler from '../../components/error/ErrorHandler';

export default function ObligationFormCalcRow() {
  const { moduleBridgeHex, plantHex } = useParams();
  const isMounted = useIsMounted();
  const dispatch = useDispatch();
  const [showModal, setShowModal] = useState(false);
  const [finished, setFinished] = useState(false);
  const [error, setError] = useState(false);
  const [batchProccess, setBatchProccess] = useState();
  const pendingQuestions = useSelector(
    (state) => state.currentPage?.pendingQuestions
  );
  const permissions = useSelector((state) => state.auth.permissions);

  // > BATCH LOOP
  const handleBatchLoop = useCallback(async () => {
    axios
      .post(
        axiosPath("api_r_user_m_legalCompliance_plantObligationForm_calc"),
        {
          moduleBridgeHex,
          plantHex,
          tempBatchLoopID: batchProccess?.tempBatchLoopID || null,
        }
      )
      .then((r) => {
        if (isMounted.current) setBatchProccess(r.data);
      })
      .catch((e) => {
        setBatchProccess(undefined);
        setError(true);
        dispatch(errorHandler(e));
      });
  }, [batchProccess, moduleBridgeHex, plantHex]);

  useEffect(() => {
    if (batchProccess && !batchProccess?.isFinished) {
      handleBatchLoop();
    } else if (batchProccess && batchProccess.isFinished) {
      const alert = {
        title: ReactDOMServer.renderToString(
          <Translator file="module-legalCompliance" name="completed" />
        ),
        message: permissions?.client?.gcl?.module?.plant?.userObligationForm
          ?.autoAssign
          ? ReactDOMServer.renderToString(
              <Translator
                file="module-legalCompliance"
                name="obligations.have.been.assigned"
              />
            )
          : ReactDOMServer.renderToString(
              <Translator
                file="module-legalCompliance"
                name="obligations.have.been.calculated"
              />
            ),
      };
      AppNotificationAlert({
        title: alert.title,
        message: alert.message,
        type: "success",
        duration: 5000,
      });
      setBatchProccess(undefined);
      setFinished(true);
      // navigate(
      //     path({
      //       name: 'user_module_legalCompliance_evaluation',
      //       params: {
      //         moduleBridgeHex,
      //         plantHex,
      //         page: 1,
      //         filtersHex: 0,
      //       },
      //     })
      // );
    }
  }, [batchProccess]);
  // > BATCH LOOP

  return (
    <>
      <FloatingButtons>
        <span
          onClick={() => setShowModal(true)}
          className={`btn btn-${
            pendingQuestions?.length === 0 ? "outline-" : ""
          }info float-right mt-1 mr-2 d-inline-flex align-items-center`}
          // onClick={() => !isLoading && handleSaveDraft()}
        >
          <AppRemixIcon icon="calculator" className="mr-1" />
          {`${ReactDOMServer.renderToString(
            <Translator file="module-legalCompliance" name="calculate" />
          )} (${pendingQuestions?.length || 0} ${ReactDOMServer.renderToString(
            <Translator file="module-legalCompliance" name="new.changes" />
          )})`}
        </span>
      </FloatingButtons>
      <AppModal
        title={ReactDOMServer.renderToString(
          <Translator file="module-legalCompliance" name="form.calculation" />
        )}
        size="xl"
        show={showModal}
        isCancelBtn={false}
        isCloseButton={false}
      >
        {finished || error ? (
          <>
            <h4>
              {finished ? (
                <Translator
                  file="module-legalCompliance"
                  name="form.calculation"
                />
              ) : (
                <Translator
                  file="module-legalCompliance"
                  name="calculation.has.been.completed"
                />
              )}
            </h4>
            <ProgressBar
              animated
              now={100}
              label={
                finished
                  ? ReactDOMServer.renderToString(
                      <Translator
                        file="module-legalCompliance"
                        name="completed"
                      />
                    )
                  : `${(
                      <span>
                        <Translator
                          file="module-legalCompliance"
                          name="error"
                        />
                        !
                      </span>
                    )}`
              }
              variant={finished ? "success" : "danger"}
            />
            <div className="float-right mt-2">
              <AppLink
                path="user_module_legalCompliance_evaluation"
                params={{
                  moduleBridgeHex,
                  plantHex,
                  page: 1,
                  filtersHex: 0,
                }}
                className="btn btn-primary"
              >
                <Translator file="module-legalCompliance" name="return" />
              </AppLink>
            </div>
            <p className="text-danger mt-2">
              {error && (
                <Translator
                  file="module-legalCompliance"
                  name="calculation.not.performed.try.again.or.contact"
                />
              )}
            </p>
          </>
        ) : batchProccess ? (
          <>
            <PreventNavigation />
            <h4>
              {batchProccess?.modalInfo?.message || (
                <Translator
                  file="module-legalCompliance"
                  name="precalculating"
                />
              )}
            </h4>
            <ProgressBar
              animated
              now={batchProccess?.modalInfo?.percent || 5}
              label={`${parseInt(batchProccess?.modalInfo?.percent || 5, 10)}%`}
            />
            <div className="float-right mt-2">
              <span className="btn btn-primary disabled">
                <Translator file="module-legalCompliance" name="return" />
              </span>
            </div>
          </>
        ) : (
          <>
            <Translator
              file="module-legalCompliance"
              name="do.you.want.start.with.form.calculation"
            />
            <br />
            <Translator
              file="module-legalCompliance"
              name="take.several.minutes"
            />
            <br />
            <br />
            <small>
              <Translator
                file="module-legalCompliance"
                name="only.want.save.form.draf.all.changes.saved"
              />
            </small>
            <br />
            <br />
            <div className="float-right">
              <span
                className="btn btn-danger mr-1"
                onClick={() => setShowModal(false)}
              >
                <Translator file="module-legalCompliance" name="cancel" />
              </span>
              <span
                className="btn btn-success"
                onClick={() => setBatchProccess([])}
              >
                <Translator file="module-legalCompliance" name="start" />
              </span>
            </div>
          </>
        )}
      </AppModal>
    </>
  );
}

const FloatingButtons = styled.div`
  position: fixed;
  bottom: 0;
  right: 0;
  left: 0;
  height: 45px;
  padding-right: 15px;
  background: ${colors.bg};
  border-top: 1px solid ${colors.grey300};
`;
